import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/lib/supabase";
import DashboardSidebar from "@/pages/dashboard/components/DashboardSidebar";
import MobileBottomNav from "@/pages/dashboard/components/MobileBottomNav";
import RecipientInput, { type Recipient } from "./components/RecipientInput";
import MessageComposer, { type MsgType } from "./components/MessageComposer";
import SendSettings, { type SendConfig } from "./components/SendSettings";
import MessagePreview from "./components/MessagePreview";

const SUPABASE_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string;
const SUPABASE_ANON_KEY = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY as string;

type SendStep = "compose" | "success";

const BATCH_SIZE = 30;
const SPLIT_THRESHOLD = 30;
const AUTO_SPLIT_SIZE = 30;

// 브라우저 리로드 시에도 작성 중인 내용을 보존하기 위한 초안 키
const DRAFT_KEY = "send_draft_v1";

export default function SendPage() {
  const navigate = useNavigate();
  const { user, loading, session, perMessageRate } = useAuth();
  const [collapsed, setCollapsed] = useState(false);

  // Form state
  const [recipients, setRecipients] = useState<Recipient[]>([]);
  const [msgType, setMsgType] = useState<MsgType>("SMS");
  const [message, setMessage] = useState("");
  const [subject, setSubject] = useState("");
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [sendConfig, setSendConfig] = useState<SendConfig>({
    scheduleEnabled: false,
    scheduleDate: "",
    scheduleTime: "",
    repeatType: "none",
    repeatEndDate: "",
  });
  const [sending, setSending] = useState(false);
  const [step, setStep] = useState<SendStep>("compose");
  const [sendError, setSendError] = useState<string | null>(null);

  // Credit state
  const [credit, setCredit] = useState(0);

  // Auto-split state
  const [splitProgress, setSplitProgress] = useState<{ current: number; total: number } | null>(null);
  const [splitResults, setSplitResults] = useState<Array<{ successCount: number; failCount: number }>>([]);

  // Failed recipients retry state
  const [failedRecipients, setFailedRecipients] = useState<Recipient[]>([]);
  const [isRetry, setIsRetry] = useState(false);

  useEffect(() => {
    if (!loading && !user) {
      navigate("/login");
    }
  }, [user, loading, navigate]);

  // Fetch credit
  useEffect(() => {
    if (!user) return;
    supabase.functions.invoke("get-user-profile", { body: { user_id: user.id } })
      .then(({ data, error }) => {
        if (!error && data?.credit != null) {
          setCredit(Number(data.credit));
        }
      })
      .catch(() => {});
  }, [user]);

  // 작성 중인 초안 복원 (리로드/탭 복귀 대응, 최초 1회)
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(DRAFT_KEY);
      if (!raw) return;
      const d = JSON.parse(raw) as {
        recipients?: Recipient[];
        message?: string;
        subject?: string;
        msgType?: MsgType;
        sendConfig?: Partial<SendConfig>;
      };
      if (Array.isArray(d.recipients)) setRecipients(d.recipients);
      if (typeof d.message === "string") setMessage(d.message);
      if (typeof d.subject === "string") setSubject(d.subject);
      if (d.msgType) setMsgType(d.msgType);
      if (d.sendConfig) {
        setSendConfig({
          scheduleEnabled: false,
          scheduleDate: "",
          scheduleTime: "",
          repeatType: "none",
          repeatEndDate: "",
          ...d.sendConfig,
        });
      }
    } catch {
      // 복원 실패는 무시
    }
  }, []);

  // 초안 저장 (내용이 있을 때만)
  useEffect(() => {
    if (!message && !subject && recipients.length === 0) return;
    try {
      sessionStorage.setItem(
        DRAFT_KEY,
        JSON.stringify({ recipients, message, subject, msgType, sendConfig })
      );
    } catch {
      // 저장 실패는 무시
    }
  }, [recipients, message, subject, msgType, sendConfig]);

  // 발송 완료 시 초안 정리
  useEffect(() => {
    if (step === "success") {
      try {
        sessionStorage.removeItem(DRAFT_KEY);
      } catch {
        // 무시
      }
    }
  }, [step]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a18] flex items-center justify-center">
        <i className="ri-loader-4-line animate-spin text-violet-400 text-3xl"></i>
      </div>
    );
  }

  if (!user) return null;

  const collectFailedRecipients = async (historyId: string) => {
    try {
      const { data: failedData } = await supabase
        .from("send_recipients")
        .select("phone, name")
        .eq("history_id", historyId)
        .eq("status", "fail");
      if (failedData && failedData.length > 0) {
        setFailedRecipients(
          failedData.map((r: any) => ({
            id: crypto.randomUUID(),
            phone: r.phone,
            name: r.name || undefined,
          }))
        );
      }
    } catch (err) {
      console.error("[Send] 실패 수신자 조회 오류:", err);
    }
  };

  const handleSend = async (overrideRecipients?: Recipient[]) => {
    if (!user) return;
    // Safety: Ensure overrideRecipients is actually an array (prevents React event object leaking in)
    const targetRecipients = Array.isArray(overrideRecipients) ? overrideRecipients : recipients;

    if (targetRecipients.length === 0) {
      setSendError("수신자를 최소 1명 이상 추가해주세요.");
      return;
    }
    if (!message.trim()) {
      setSendError("메시지 내용을 입력해주세요.");
      return;
    }
    if (!session) {
      setSendError("세션이 만료되었습니다. 다시 로그인해주세요.");
      return;
    }

    setSending(true);
    setSendError(null);
    setSplitResults([]);

    const unitCost = msgType === "SMS" ? (perMessageRate ?? 20) : msgType === "LMS" ? 50 : 100;
    const isScheduled = sendConfig.scheduleEnabled && sendConfig.scheduleDate && sendConfig.scheduleTime;
    const scheduledAt = isScheduled
      ? new Date(`${sendConfig.scheduleDate}T${sendConfig.scheduleTime}:00`).toISOString()
      : null;
    const accessToken = session.access_token;

    // --- Auto-split mode: single job processed in the background ---
    const needsAutoSplit = !isScheduled && targetRecipients.length >= SPLIT_THRESHOLD;
    if (needsAutoSplit) {
      const totalCost = unitCost * targetRecipients.length;
      try {
        const resp = await fetch(
          `${SUPABASE_URL}/functions/v1/send-sms`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              apikey: SUPABASE_ANON_KEY,
              Authorization: `Bearer ${accessToken}`,
            },
            body: JSON.stringify({
              user_id: user.id,
              recipients: targetRecipients.map((r) => ({ phone: r.phone, name: r.name })),
              body: message,
              subject: subject || undefined,
              cost: totalCost,
              type: msgType,
              has_image: !!imageFile,
              split_mode: true,
            }),
          }
        );

        if (!resp.ok) {
          const err = await resp.json().catch(() => ({ error: "Unknown error" }));
          throw new Error(err.error || `분할 발송 등록 실패 (HTTP ${resp.status})`);
        }

        const result = await resp.json();
        const historyId = result.historyId as string;

        if (result.remaining_credit != null) {
          setCredit(Number(result.remaining_credit));
        }

        // Drive the background processor and poll progress until done (or timeout).
        setSplitProgress({ current: 0, total: targetRecipients.length });
        const pollStart = Date.now();
        const maxPollMs = 3 * 60 * 1000;
        let finished = false;

        while (Date.now() - pollStart < maxPollMs) {
          try {
            await supabase.functions.invoke("process-split-sends", {});
          } catch (e) {
            console.error("[Send] process-split-sends invoke error:", e);
          }

          const { data: hData } = await supabase
            .from("send_history")
            .select("status, success_count, fail_count")
            .eq("id", historyId)
            .maybeSingle();

          if (hData) {
            const done = (hData.success_count ?? 0) + (hData.fail_count ?? 0);
            setSplitProgress({ current: done, total: targetRecipients.length });
            if (hData.status === "success" || hData.status === "fail") {
              finished = true;
              setSplitResults([{ successCount: hData.success_count ?? 0, failCount: hData.fail_count ?? 0 }]);
              await collectFailedRecipients(historyId);
              break;
            }
          }

          await new Promise((r) => setTimeout(r, 2000));
        }

        setSplitProgress(null);

        if (!finished) {
          setSending(false);
          setSendError("분할 발송이 아직 백그라운드에서 처리 중입니다. 잠시 후 발송 내역에서 확인해주세요.");
          return;
        }

        setStep("success");
        setSending(false);
      } catch (err: any) {
        console.error("[Send] 분할 발송 오류:", err);
        setSendError(err?.message || "분할 발송 중 오류가 발생했습니다.");
        setSending(false);
      }
      return;
    }

    // --- Regular (single) send ---
    try {
      const cost = unitCost * targetRecipients.length;

      const resp = await fetch(
        `${SUPABASE_URL}/functions/v1/send-sms`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            apikey: SUPABASE_ANON_KEY,
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            user_id: user.id,
            recipients: targetRecipients.map((r) => ({ phone: r.phone, name: r.name })),
            body: message,
            subject: subject || undefined,
            cost,
            is_scheduled: isScheduled,
            scheduled_at: scheduledAt,
            type: msgType,
            has_image: !!imageFile,
          }),
        }
      );

      if (!resp.ok) {
        const err = await resp.json().catch(() => ({ error: "Unknown error" }));
        const detailMsg = err.detail ? `\n상세: ${err.detail}` : "";
        throw new Error((err.error || `발송 실패 (HTTP ${resp.status})`) + detailMsg);
      }

      const result = await resp.json();

      // Update credit from response
      if (result.remaining_credit != null) {
        setCredit(Number(result.remaining_credit));
      }

      if (result.originatorReplaced || result.warning) {
        console.warn("[Send] originator replaced:", result.warning);
        setSendError(result.warning || "발신번호가 자동으로 변경되었습니다. MessageBird에서 등록된 번호를 확인하세요.");
        setTimeout(() => {
          setStep("success");
        }, 3000);
        return;
      }

      // For single retry, also check for failed recipients
      if (isRetry && result.historyId && result.failCount > 0) {
        try {
          const { data: failedData } = await supabase
            .from("send_recipients")
            .select("phone, name")
            .eq("history_id", result.historyId)
            .eq("status", "fail");
          if (failedData && failedData.length > 0) {
            const deduped = new Map<string, Recipient>();
            failedData.forEach((r: any) => {
              const key = r.phone?.replace(/\D/g, "") || "";
              if (key && !deduped.has(key)) {
                deduped.set(key, {
                  id: crypto.randomUUID(),
                  phone: r.phone,
                  name: r.name || undefined,
                });
              }
            });
            setFailedRecipients(Array.from(deduped.values()));
          }
        } catch (err) {
          console.error("[Send] 실패 수신자 조회 오류:", err);
        }
      }

      setStep("success");
    } catch (err: any) {
      console.error("[Send] 발송 오류:", err);
      setSendError(err?.message || "발송 중 오류가 발생했습니다.");
    } finally {
      setSending(false);
    }
  };

  const handleRetryFailed = () => {
    if (failedRecipients.length === 0) return;
    setIsRetry(true);
    setSplitResults([]);
    setSendError(null);
    handleSend(failedRecipients);
  };

  const handleReset = () => {
    try {
      sessionStorage.removeItem(DRAFT_KEY);
    } catch {
      // 무시
    }
    setRecipients([]);
    setMessage("");
    setSubject("");
    setImageFile(null);
    setMsgType("SMS");
    setSendError(null);
    setSplitProgress(null);
    setSplitResults([]);
    setFailedRecipients([]);
    setIsRetry(false);
    setSendConfig({
      scheduleEnabled: false,
      scheduleDate: "",
      scheduleTime: "",
      repeatType: "none",
      repeatEndDate: "",
    });
    setStep("compose");
  };

  const sidebarWidth = collapsed ? "md:ml-16" : "md:ml-60";
  const totalCost = (msgType === "SMS" ? (perMessageRate ?? 20) : msgType === "LMS" ? 50 : 100) * recipients.length;
  const batchCount = Math.ceil(recipients.length / BATCH_SIZE);
  const showBatchInfo = recipients.length > BATCH_SIZE;
  const isAutoSplit = !sendConfig.scheduleEnabled && recipients.length >= SPLIT_THRESHOLD;
  const autoSplitChunks = isAutoSplit ? Math.ceil(recipients.length / AUTO_SPLIT_SIZE) : 0;

  const totalFailCount = splitResults.reduce((sum, r) => sum + r.failCount, 0);

  return (
    <div className="min-h-screen bg-[#080812] flex">
      <DashboardSidebar collapsed={collapsed} onToggle={() => setCollapsed(!collapsed)} />

      <main className={`flex-1 ${sidebarWidth} transition-all duration-300 min-h-screen pb-20 md:pb-0`}>
        {/* Top bar */}
        <header className="h-14 md:h-16 bg-[#0a0a18]/80 backdrop-blur-md border-b border-white/[0.06] flex items-center justify-between px-4 md:px-6 sticky top-0 z-30">
          <div className="flex items-center gap-2 md:gap-3">
            <button
              onClick={() => navigate("/dashboard")}
              className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-white cursor-pointer transition-colors"
            >
              <i className="ri-arrow-left-line text-lg"></i>
            </button>
            <div>
              <h1 className="text-white font-semibold text-sm md:text-base">문자 발송</h1>
              <p className="text-gray-500 text-[10px] md:text-xs hidden sm:block">SMS / LMS / MMS 발송</p>
            </div>
          </div>
          <div className="flex items-center gap-2 md:gap-3">
            {recipients.length > 0 && (
              <div className="flex items-center gap-1.5 md:gap-2 bg-white/[0.05] border border-white/[0.08] rounded-lg px-2 md:px-3 py-1 md:py-1.5">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-group-line text-violet-400 text-xs md:text-sm"></i>
                </div>
                <span className="text-white text-[10px] md:text-xs font-semibold">{recipients.length}명</span>
                <span className="text-gray-500 text-[10px] md:text-xs hidden sm:inline">·</span>
                <span className="text-amber-400 text-[10px] md:text-xs font-semibold hidden sm:inline">₩{totalCost.toLocaleString()}</span>
              </div>
            )}
            {isAutoSplit && (
              <span className="hidden sm:inline-flex items-center gap-1 bg-emerald-500/10 border border-emerald-500/25 text-emerald-400 text-[10px] md:text-xs font-medium px-1.5 md:px-2 py-1 md:py-1.5 rounded-lg whitespace-nowrap">
                <i className="ri-split-cells-horizontal text-xs"></i>
                {autoSplitChunks}회 분할
              </span>
            )}
            <button
              onClick={() => navigate("/dashboard/history")}
              className="flex items-center gap-1 md:gap-1.5 bg-white/[0.05] border border-white/[0.08] text-gray-300 hover:text-white px-2 md:px-3 py-1.5 md:py-2 rounded-lg text-[10px] md:text-xs font-medium cursor-pointer whitespace-nowrap transition-all"
            >
              <i className="ri-history-line text-xs md:text-sm"></i>
              <span className="hidden sm:inline">발송 내역</span>
            </button>
          </div>
        </header>

        {/* Mobile cost bar */}
        {recipients.length > 0 && (
          <div className="sm:hidden px-4 py-2 bg-white/[0.02] border-b border-white/[0.04] flex items-center justify-between">
            <span className="text-gray-500 text-[10px]">예상 비용</span>
            <span className="text-amber-400 text-xs font-bold">₩{totalCost.toLocaleString()}</span>
          </div>
        )}

        {/* Success screen */}
        {step === "success" ? (
          <div className="flex items-center justify-center min-h-[calc(100vh-56px-80px)] md:min-h-[calc(100vh-64px)] p-4 md:p-6">
            <div className="max-w-md w-full text-center">
              {/* Icon changes based on retry / partial success */}
              <div className={`w-16 h-16 md:w-20 md:h-20 flex items-center justify-center rounded-full mx-auto mb-4 md:mb-6 ${
                failedRecipients.length > 0
                  ? "bg-gradient-to-br from-amber-500/30 to-orange-500/30 border border-amber-500/30"
                  : "bg-gradient-to-br from-emerald-500/30 to-teal-500/30 border border-emerald-500/30"
              }`}>
                {failedRecipients.length > 0 ? (
                  <i className="ri-error-warning-line text-amber-400 text-3xl md:text-4xl"></i>
                ) : (
                  <i className="ri-checkbox-circle-fill text-emerald-400 text-3xl md:text-4xl"></i>
                )}
              </div>

              <h2 className="text-white text-xl md:text-2xl font-bold mb-2">
                {sendConfig.scheduleEnabled
                  ? "예약 발송 등록 완료!"
                  : isRetry
                    ? failedRecipients.length > 0 ? "재발송 완료 — 일부 실패" : "재발송 완료!"
                    : "발송 완료!"}
              </h2>
              <p className="text-gray-400 text-xs md:text-sm mb-6 md:mb-8 leading-relaxed">
                {sendConfig.scheduleEnabled
                  ? `${sendConfig.scheduleDate} ${sendConfig.scheduleTime}에 ${recipients.length}명에게 발송 예약되었습니다.`
                  : isRetry
                    ? failedRecipients.length > 0
                      ? `${failedRecipients.length}건의 실패 수신자 재발송을 완료했지만, 여전히 일부 실패가 있습니다.`
                      : `실패 수신자 ${failedRecipients.length}건 재발송이 완료되었습니다.`
                    : splitResults.length > 0
                      ? `${recipients.length.toLocaleString()}명에게 자동 분할 발송이 완료되었습니다.`
                      : showBatchInfo
                        ? `${recipients.length}명에게 ${msgType} 메시지가 ${BATCH_SIZE}건씩 ${batchCount}개 배치로 분할 발송 완료되었습니다.`
                        : `${recipients.length}명에게 ${msgType} 메시지가 성공적으로 발송되었습니다.`}
              </p>

              <div className="bg-white/[0.03] border border-white/[0.08] rounded-2xl p-4 md:p-5 mb-6 md:mb-8 text-left space-y-2 md:space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-400 text-xs md:text-sm">메시지 유형</span>
                  <span className="text-white text-xs md:text-sm font-semibold">{msgType}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400 text-xs md:text-sm">{isRetry ? "재발송 대상" : "발송 수신자"}</span>
                  <div className="text-right">
                    <span className="text-white text-xs md:text-sm font-semibold">{(isRetry ? failedRecipients.length : recipients.length).toLocaleString()}명</span>
                    {splitResults.length > 0 && (
                      <p className="text-emerald-400 text-[10px] md:text-xs mt-0.5">
                        {AUTO_SPLIT_SIZE.toLocaleString()}명씩 자동 분할 발송
                      </p>
                    )}
                    {showBatchInfo && splitResults.length === 0 && !isRetry && (
                      <p className="text-violet-400 text-[10px] md:text-xs mt-0.5">{BATCH_SIZE}건씩 {batchCount}개 배치</p>
                    )}
                  </div>
                </div>

                {/* Split result details */}
                {splitResults.length > 0 && (
                  <div className="border-t border-white/[0.06] pt-2 md:pt-3 mt-2">
                    <span className="text-gray-400 text-[10px] md:text-xs font-medium block mb-2">분할 발송 결과</span>
                    <div className="space-y-1.5 max-h-36 overflow-y-auto">
                      {splitResults.map((r, i) => (
                        <div key={i} className="flex items-center justify-between text-[10px] md:text-xs">
                          <span className="text-gray-500">전체 결과</span>
                          <div className="flex items-center gap-2 md:gap-3">
                            <span className="text-emerald-400">
                              <i className="ri-check-line text-[10px]"></i> {r.successCount.toLocaleString()}건
                            </span>
                            {r.failCount > 0 && (
                              <span className="text-red-400">
                                <i className="ri-close-line text-[10px]"></i> {r.failCount.toLocaleString()}건
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Failed recipients summary */}
                {failedRecipients.length > 0 && (
                  <div className="border-t border-white/[0.06] pt-2 md:pt-3 mt-2">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-gray-400 text-[10px] md:text-xs font-medium">실패 수신자</span>
                      <span className="text-red-400 text-[10px] md:text-xs font-semibold">{failedRecipients.length.toLocaleString()}명</span>
                    </div>
                    <div className="max-h-28 overflow-y-auto space-y-1">
                      {failedRecipients.slice(0, 5).map((r) => (
                        <div key={r.id} className="flex items-center gap-2 text-[10px] md:text-xs">
                          <span className="text-gray-600 w-3.5 md:w-4 flex items-center justify-center shrink-0">
                            <i className="ri-close-circle-fill text-red-400/60 text-[10px]"></i>
                          </span>
                          <span className="text-gray-300 truncate">{r.name || "이름 없음"}</span>
                          <span className="text-gray-500 font-mono">{r.phone}</span>
                        </div>
                      ))}
                      {failedRecipients.length > 5 && (
                        <p className="text-gray-600 text-[10px] md:text-xs pl-5">외 {failedRecipients.length - 5}명...</p>
                      )}
                    </div>
                  </div>
                )}

                <div className="flex justify-between">
                  <span className="text-gray-400 text-xs md:text-sm">차감 금액</span>
                  <span className="text-amber-400 text-xs md:text-sm font-bold">₩{totalCost.toLocaleString()}</span>
                </div>
              </div>

              <div className="flex flex-col gap-2 md:gap-3">
                {/* Retry button for failed recipients */}
                {failedRecipients.length > 0 && (
                  <button
                    onClick={handleRetryFailed}
                    className="w-full py-2.5 md:py-3 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-white text-sm font-bold cursor-pointer whitespace-nowrap transition-all flex items-center justify-center gap-2 hover:scale-[1.02] active:scale-[0.98]"
                  >
                    <i className="ri-refresh-line text-base"></i>
                    실패 {failedRecipients.length.toLocaleString()}건 재발송
                  </button>
                )}
                <div className="flex gap-2 md:gap-3">
                  <button
                    onClick={handleReset}
                    className={`flex-1 py-2.5 md:py-3 rounded-xl text-sm font-bold cursor-pointer whitespace-nowrap transition-all flex items-center justify-center gap-2 ${
                      failedRecipients.length > 0
                        ? "bg-white/[0.05] border border-white/[0.08] text-gray-300 hover:text-white"
                        : "bg-gradient-to-r from-violet-600 to-pink-600 hover:from-violet-500 hover:to-pink-500 text-white hover:scale-[1.02] active:scale-[0.98]"
                    }`}
                  >
                    <i className="ri-send-plane-line text-base"></i>
                    새 메시지 작성
                  </button>
                  <button
                    onClick={() => navigate("/dashboard")}
                    className="flex-1 py-2.5 md:py-3 rounded-xl bg-white/[0.05] border border-white/[0.08] text-gray-300 hover:text-white text-sm font-semibold cursor-pointer whitespace-nowrap transition-all"
                  >
                    대시보드로
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : (
          /* Compose screen */
          <div className="p-3 md:p-6">
            {/* Error banner */}
            {sendError && (
              <div className="mb-3 md:mb-5 bg-red-500/10 border border-red-500/30 rounded-xl px-3 md:px-4 py-2.5 md:py-3 flex items-start gap-2 md:gap-3">
                <div className="w-4 h-4 md:w-5 md:h-5 flex items-center justify-center shrink-0 mt-0.5">
                  <i className="ri-error-warning-line text-red-400 text-xs md:text-sm"></i>
                </div>
                <p className="text-red-300 text-xs md:text-sm">{sendError}</p>
              </div>
            )}

            {/* Auto-split progress banner */}
            {splitProgress && splitProgress.total > 0 && (
              <div className="mb-3 md:mb-5 bg-emerald-500/10 border border-emerald-500/25 rounded-xl px-3 md:px-4 py-2.5 md:py-3 flex items-center gap-2 md:gap-3">
                <div className="w-5 h-5 md:w-6 md:h-6 flex items-center justify-center shrink-0">
                  <i className="ri-loader-4-line animate-spin text-emerald-400 text-sm md:text-base"></i>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-emerald-300 text-xs md:text-sm font-semibold">
                    {isRetry ? `실패 수신자 재발송 중` : `자동 분할 발송 중`} — {splitProgress.current}/{splitProgress.total}
                  </p>
                  <div className="mt-1.5 h-1.5 bg-white/[0.08] rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full transition-all duration-500"
                      style={{ width: `${Math.round((splitProgress.current / splitProgress.total) * 100)}%` }}
                    />
                  </div>
                  <p className="text-gray-500 text-[10px] md:text-xs mt-1">
                    {AUTO_SPLIT_SIZE.toLocaleString()}명씩 {splitProgress.total}회 분할 발송 중입니다. 페이지를 닫지 마세요.
                  </p>
                </div>
              </div>
            )}

            {/* Desktop: grid layout / Mobile: single column stack */}
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 md:gap-6">
              {/* Left: Recipient + Message + Settings */}
              <div className="xl:col-span-2 space-y-3 md:space-y-5">
                <RecipientInput recipients={recipients} onChange={setRecipients} userId={user.id} />
                <MessageComposer
                  msgType={msgType}
                  onMsgTypeChange={setMsgType}
                  message={message}
                  onMessageChange={setMessage}
                  subject={subject}
                  onSubjectChange={setSubject}
                  imageFile={imageFile}
                  onImageChange={setImageFile}
                  perMessageRate={perMessageRate}
                />
                <SendSettings config={sendConfig} onChange={setSendConfig} userId={user.id} />
              </div>

              {/* Right: Preview + Summary (desktop: sticky, mobile: bottom of flow) */}
              <div className="xl:col-span-1">
                <div className="xl:sticky xl:top-24">
                  <MessagePreview
                    msgType={msgType}
                    message={message}
                    subject={subject}
                    imageFile={imageFile}
                    recipients={recipients}
                    scheduleEnabled={sendConfig.scheduleEnabled}
                    scheduleDate={sendConfig.scheduleDate}
                    scheduleTime={sendConfig.scheduleTime}
                    repeatType={sendConfig.repeatType}
                    repeatEndDate={sendConfig.repeatEndDate}
                    perMessageRate={perMessageRate}
                    credit={credit}
                    batchSize={BATCH_SIZE}
                    splitThreshold={SPLIT_THRESHOLD}
                    autoSplitSize={AUTO_SPLIT_SIZE}
                    splitProgress={splitProgress}
                    onSend={handleSend}
                    sending={sending}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </main>

      <MobileBottomNav />
    </div>
  );
}